/// <amd-module name="Dashboards/DashboardPickerProvider/DashboardPickerProviderFactory" />
import { Dashboard } from "Dashboards/Common/Contracts/Dashboard";
import { DashboardFavoriteArtifactProperties } from "Dashboards/Common/Contracts/DashboardFavoriteArtifactProperties";
import { CreateDashboardPanel } from "Dashboards/DashboardPanel/CreateDashboardPanel";
import * as Resources from "Dashboards/DashboardPickerProvider/Resources";
import { FavoriteStorageScopes } from "Favorites/Common/Constants";
import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import { IFavoriteProviderDataService } from "Favorites/Common/Contracts/Service";
import { ArtifactPickerProvider, IArtifactPickerProvider, IArtifactPickerProviderOptions } from "Favorites/Picker/ArtifactPickerProvider";
import * as React from "react";
import { caseInsensitiveContains, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IPickListAction, IPickListGroup } from "VSSUI/Components/PickList/PickList.Props";
import { VssIconType } from "VSSUI/VssIcon";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface PickerDashboardItem {
    owner: DashboardOwner;
    dashboard: { id: string; name: string; ownerId: string; groupId?: string };
}

export interface Project {
    id: string;
    name: string;
}

export interface DashboardOwner {
    id: string;
    name: string;
}

interface WrappedTeam {
    value: DashboardOwner;
}

/**
 * An interface composed of a subset of properties copied from CreatedDashboardItem in CreateDashboardDialogModels.ts
 * in the old web platform. Used for interacting with the results of the new dashboard dialog which is rendered using a legacy component.
 */
interface NewDashboardItem {
    /**
     * ID of the Dashboard. Provided by service at creation time.
     */
    id: string;
    /**
     * The team ID.
     */
    teamId: string;
    /**
     * The team project ID.
     */
    projectId: string;
}

/** Responsible for constructing a dashboards IArtifactPickerProvider */
export class DashboardPickerProviderFactory {
    public static readonly DashboardFavoriteType: string = "Microsoft.TeamFoundation.Dashboards.Dashboard";

    constructor(private pageContext: IVssPageContext, private project: Project, private currentDashboard: PickerDashboardItem | undefined) {}

    /**
     * Returns an artifact picker provider used to show a list of dashboards that when clicked navigate the user to the chosen dashboard.
     * @param showBrowseDashboards A value of true shows an entry at the bottom of the picker when expanded that when clicked takes the user to the dashboards directory
     * @param canCreateDashboards A value of true shows an entry at the bottom of the picker when expanded that when clicked opens the new dashboard dialog
     */
    public build(showBrowseDashboards: boolean = false, canCreateDashboards: boolean = false): IArtifactPickerProvider<PickerDashboardItem> {
        return this.getArtifactPicker(this.currentDashboard, showBrowseDashboards, canCreateDashboards);
    }

    private getArtifactPicker(
        currentDashboard: PickerDashboardItem | undefined,
        showBrowseDashboards: boolean,
        canCreateDashboards: boolean
    ): IArtifactPickerProvider<PickerDashboardItem> {
        const browseAllCallback = () => {
            const locationService: IVssLocationService = this.pageContext.getService("IVssLocationService");
            const url = locationService.routeUrl("ms.vss-dashboards-web.dashboards-directory-route", {
                ["project"]: this.project.name
            });
            FastPageSwitch(this.pageContext, url, true);
        };

        const props: IArtifactPickerProviderOptions<PickerDashboardItem> = {
            pageContext: this.pageContext,
            favoritesContext: {
                artifactType: DashboardPickerProviderFactory.DashboardFavoriteType,
                artifactScope: {
                    id: this.project.id,
                    name: this.project.name,
                    type: FavoriteStorageScopes.Project
                }
            },
            selectedArtifact: currentDashboard,
            artifactComparer: (item1, item2) => {
                return localeIgnoreCaseComparer(this.getArtifactName(item1), this.getArtifactName(item2));
            },
            getSearchResults: (searchText) => {
                return getMyDashboardsItems(this.pageContext).then((items) => {
                    return items.filter((item) => caseInsensitiveContains(this.getArtifactName(item), searchText));
                });
            },
            searchNoResultsText: Resources.DashboardsPicker_SearchNoResultsText,
            searchTextPlaceholder: Resources.DashboardsPicker_SearchTextPlaceholder,
            browseAllText: Resources.DashboardsPicker_BrowseAllDashboardsText,
            loadingText: Resources.DashboardsPicker_LoadingDashboardsText,
            searchResultsLoadingText: Resources.DashboardsPicker_SearchResultsLoadingText,
            onBrowseAllClick: typeof showBrowseDashboards === "undefined" || showBrowseDashboards ? browseAllCallback : undefined,
            otherActions: this.getOtherActionsForPicker(canCreateDashboards),
            groups: getMyTeamGroups(this.pageContext),
            getArtifacts: () => getMyDashboardsItems(this.pageContext),
            getArtifactId: (dashboardItem) => {
                return dashboardItem.dashboard.id;
            },
            getArtifactName: (dashboardItem) => {
                return this.getArtifactName(dashboardItem);
            },
            getFavoriteFromArtifact: (dashboardItem) => this.getFavoriteForDashboard(dashboardItem),
            getArtifactFromFavorite: (favoriteItem) => this.getDashboardForFavorite(favoriteItem),
            getArtifactIcon: () => {
                return {
                    iconName: "ViewDashboard",
                    iconType: VssIconType.fabric
                };
            },
            getArtifactHref: (dashboardItem) => {
                return this.getDashboardUrl(dashboardItem);
            },
            onArtifactClicked: (dashboardItem) => {
                const url = this.getDashboardUrl(dashboardItem);
                FastPageSwitch(this.pageContext, url, true);
                return true;
            },
            favoriteGroupHeader: Resources.DashboardsPicker_FavoriteGroupHeader,
            defaultGroupHeader: Resources.DashboardsPicker_DefaultGroupHeader,
            getArtifactListGroupId: (dashboardItem) => {
                if (dashboardItem) {
                    return dashboardItem.owner.id;
                }

                return "";
            }
        };

        return new ArtifactPickerProvider(props);
    }

    private getDashboardUrl(dashboardItem: PickerDashboardItem): string {
        // Generate link to dashboard.
        const locationService: IVssLocationService = this.pageContext.getService("IVssLocationService");
        const routeValues: { [key: string]: string } = {
            project: this.project.name,
            id: dashboardItem.dashboard.id
        };

        return locationService.routeUrl("ms.vss-dashboards-web.dashboards-new-experience-route", routeValues);
    }

    private getArtifactName(dashboardItem: PickerDashboardItem): string {
        const fav = this.getFavoriteForDashboard(dashboardItem);
        const favProviderService = this.pageContext.getService<IFavoriteProviderDataService>("dashboard-favorite-provider-data-service");
        if (favProviderService != null && favProviderService.getDisplayName) {
            return favProviderService.getDisplayName(fav);
        }

        return dashboardItem.dashboard.name;
    }

    private getOtherActionsForPicker(showNewDashboard: boolean): IPickListAction[] {
        if (showNewDashboard) {
            return [
                {
                    name: Resources.DashboardsPicker_NewDashboard,
                    iconProps: {
                        iconName: "CalculatorAddition",
                        iconType: VssIconType.fabric
                    },
                    onClick: this.onNewDashboardClick
                }
            ];
        }

        return [];
    }

    private onNewDashboardClick = () => {
        const layoutManager: IVssLayoutManager = this.pageContext.getService("IVssLayoutManager");
        const onDashboardCreated = (newDashboard: NewDashboardItem) => {
            const locationService: IVssLocationService = this.pageContext.getService("IVssLocationService");

            const routeValues: { [key: string]: string } = {
                project: this.project.name,
                id: newDashboard.id
            };

            if (newDashboard.teamId) {
                routeValues["team"] = newDashboard.teamId;
            }

            const url = locationService.routeUrl("ms.vss-dashboards-web.dashboards-new-experience-route", routeValues);

            /* Publish telemetry when dashboard is created successfully by clicking on New Dashboard option in Picker*/
            const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("Dashboards", "Dashboards.Create", {
                Scenario: "DashboardPicker.CreateDashboardSuccess",
                NewDashboardId: newDashboard.id
            })


            FastPageSwitch(this.pageContext, url, true);
        };

        if (isFeatureEnabled(this.pageContext, "ms.vss-dashboards.new-dashboard-management-experience-feature", false)) {
            Promise.all([getMyTeams(this.pageContext), getAllTeams(this.pageContext)]).then(([userTeams, allWrappedTeams]) => {
                layoutManager.renderCallout((onDismiss) => {
                    return React.createElement(CreateDashboardPanel, {
                        onDismiss: onDismiss,
                        onDashboardCreated,
                        project: this.project,
                        userTeams: userTeams!,
                        allTeams: allWrappedTeams!.map((wrappedTeam: WrappedTeam) => wrappedTeam.value)
                    });
                });
            });
        } else {
            layoutManager.renderCallout((onDismiss: () => void) =>
                React.createElement(LegacyComponent, {
                    modules: ["Dashboards/Components/Shared/CreateDashboardDialog/CreateDashboardDialog"],
                    wrappedType: "CreateDashboardDialog",
                    allowTeamSelection: true,
                    onDashboardCreated,
                    onDismiss: onDismiss
                })
            );
        }
    };

    private getFavoriteForDashboard(dashboardItem: PickerDashboardItem): Favorite {
        const artifactProperties: DashboardFavoriteArtifactProperties = {
            ownerId: dashboardItem.owner.id,
            ownerName: dashboardItem.owner.name,
            groupId: dashboardItem.dashboard.groupId
        };

        const favoriteCreateParams: FavoriteCreateParameters = {
            artifactId: dashboardItem.dashboard.id,
            artifactName: dashboardItem.dashboard.name,
            artifactType: DashboardPickerProviderFactory.DashboardFavoriteType,
            artifactScope: {
                id: this.project.id,
                name: this.project.name,
                type: FavoriteStorageScopes.Project
            },
            artifactProperties
        };

        return {
            ...favoriteCreateParams,

            artifactIsDeleted: false,
            creationDate: new Date(),
            id: "",
            owner: {} as IdentityRef,
            url: "",
            _links: undefined
        };
    }

    private getDashboardForFavorite(favoriteItem: Favorite): PickerDashboardItem {
        const artifactProperties = favoriteItem.artifactProperties as DashboardFavoriteArtifactProperties;

        const owner: DashboardOwner = {
            id: artifactProperties.ownerId,
            name: artifactProperties.ownerName
        };

        return {
            owner,
            dashboard: {
                id: favoriteItem.artifactId,
                name: favoriteItem.artifactName,
                ownerId: owner.id,
                groupId: artifactProperties.groupId
            }
        };
    }
}

export function getAllTeams(pageContext: IVssPageContext): Promise<Array<{ value: DashboardOwner }> | undefined> {
    return getDataProviderData<WrappedTeam[]>(
        pageContext,
        "ms.vss-dashboards-web.dashboards-team-list-data-provider",
        "Failed to retrieve 'all teams'."
    );
}

export function getMyTeams(pageContext: IVssPageContext): Promise<DashboardOwner[] | undefined> {
    return getDataProviderData<DashboardOwner[]>(
        pageContext,
        "ms.vss-dashboards-web.dashboards-team-memberships-provider",
        "Failed to retrieve 'my teams'."
    );
}

export function getMyDashboards(pageContext: IVssPageContext): Promise<Dashboard[] | undefined> {
    return getDataProviderData<Dashboard[]>(pageContext, "ms.vss-dashboards-web.my-dashboards-data-provider", "Failed to retrieve 'my dashboards'.");
}

/** Retrieves page data from a data provider.
 *  If the provider failed to return any data, the given error message is thrown with the data provider exception message. */
async function getDataProviderData<T>(pageContext: IVssPageContext, contributionId: string, errorMessage: string): Promise<T> {
    const contributionService: IVssContributionService = pageContext.getService("IVssContributionService");
    const data = await contributionService.getDataAsync<T>(
        contributionId,
        undefined /* requestParameters */,
        true /* ignoreCache - The result will still be remembered for subsequent calls that want the same data without ignoring the cache. */
    );

    if (!data) {
        const dataProviderExceptions: { [contributionId: string]: IServerException } = contributionService.getDataProviderExceptions();
        const ex = dataProviderExceptions[contributionId];
        const message = [errorMessage, ex && ex.message].join(" ");
        throw new Error(message);
    }

    return data;
}

export function getMyTeamGroups(pageContext: IVssPageContext): Promise<IPickListGroup[]> {
    return getMyTeams(pageContext).then((teams) => {
        return packTeamsAsGroups(teams);
    });
}

export function packTeamsAsGroups(teams: DashboardOwner[] | undefined): IPickListGroup[] {
    const groups: IPickListGroup[] = [];
    if (teams) {
        teams.forEach((m) => {
            groups.push({
                key: m.id,
                name: m.name
            });
        });
        return groups.sort((a, b) => localeIgnoreCaseComparer(a.name!, b.name!));
    } else {
        return [];
    }
}

export function getMyDashboardsItems(pageContext: IVssPageContext): Promise<PickerDashboardItem[]> {
    const dashboardsPromise = getMyDashboards(pageContext);
    const teamsPromise = getMyTeams(pageContext);

    return Promise.all([dashboardsPromise, teamsPromise]).then(([dashboards, teams]) => {
        return packDashboards(dashboards as Dashboard[], teams);
    });
}

export function packDashboards(dashboards: Dashboard[] | undefined, teams: DashboardOwner[] | undefined) {
    const dashboardItems: PickerDashboardItem[] = [];
    if (dashboards && teams) {
        for (const team of teams) {
            dashboardItems.push(
                ...dashboards
                    .filter((dashboard) => dashboard.ownerId === team.id)
                    .map((dashboard) => {
                        return createPickerDashboardItem(dashboard, team);
                    })
            );
        }
    }
    return dashboardItems;
}

export function createPickerDashboardItem(dashboard: Dashboard, owner: DashboardOwner): PickerDashboardItem {
    return {
        owner: owner,
        dashboard: dashboard
    };
}
